import {ABILITY_CAST_COLORS as COLORS, ABILITY_CAST_TEXTURE_KEYS} from '../../configs/abilityCastAnimation';
import {ARROW_CAST, BOULDER_CAST, COMBAT_CAST_COLORS as FX, MELEE_SLASH_CAST, resolveMeleeImpactCast} from '../../configs/combatCastAnimation';
import {CELL_OUTLINE_TEXTURE_KEYS} from '../../configs/fieldCellOutlines';
import {MELEE_ANIMATION, MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {HEX_WAVE_TEXTURE_KEYS} from '../../configs/unitRecruitmentAnimation';
import {
  clamp01, createElement, easeInOutSine, easeOutCubic, fadeInOut, fadeOutFrom, lerp, mixColor,
} from './buildAbilityCast';
import {HEX_WAVE_RADIUS_PX, HEX_WAVE_TEXTURE_SIZE, resolveHexWaveScale} from './hexWaveTextures';
import {resolveMeleeSlashState} from './buildMeleeTimeline';

/**
 * ПЛАНЫ СВЕТА И ОГНЯ БОЯ (см. configs/combatCastAnimation): что рисуется поверх ударов и
 * залпов. Только вид: на итог боя и его время не влияют. Случайность — из rng вызывающего.
 */

const TWO_PI = Math.PI * 2;
const STREAK = ABILITY_CAST_TEXTURE_KEYS.STREAK;
const STREAK_ASPECT = 4;

const finishPlan = ({elements, shake = null, impactMs = 0}) => ({
  totalMs: elements.reduce((latest, element) => Math.max(latest, element.endMs), 1),
  impactMs,
  elements,
  outlines: [],
  shake,
});

/** Светлый оттенок цвета: на темных цветах игрока ядро все равно светится */
const lighten = (color, share = 0.6) => mixColor(color, COLORS.white, share);

/** Разовая вспышка: разрастается и гаснет */
export const pushFlash = (elements, {at, textureKey = MELEE_TEXTURE_KEYS.GLOW, tint, sizePx, startMs = 0, durationMs, alpha = 1, order = 0, layer, fromScale = 0.35, rotation = 0, blend}) => {
  elements.push(createElement({
    textureKey, tint, sizePx, startMs, durationMs, order, layer, blend,
    pose: (local) => ({...at, scale: lerp(fromScale, 1, easeOutCubic(local)), alpha: alpha * (1 - local * local), rotation}),
  }));
};

/**
 * Искра-черточка: летит по направлению и смотрит туда же, хвост у нее сзади (картинка —
 * яркой головой вправо), поэтому читается как след, а не как горошина
 */
export const pushStreak = (elements, {at, angle, distance, lengthPx, thicknessPx, tint, startMs = 0, lifeMs, order = 0, squash = 1, alpha = 1}) => {
  const toX = Math.cos(angle) * distance;
  const toY = Math.sin(angle) * distance * squash;
  const flyAngle = Math.atan2(toY, toX);
  elements.push(createElement({
    textureKey: STREAK, tint, sizePx: lengthPx, startMs, durationMs: lifeMs, order,
    pose: (local) => {
      const travel = easeOutCubic(local);
      return {
        x: at.x + toX * travel,
        y: at.y + toY * travel,
        scaleX: lerp(1, 0.45, local),
        scaleY: (thicknessPx * STREAK_ASPECT) / lengthPx,
        alpha: alpha * fadeOutFrom(local, 0.5),
        rotation: flyAngle,
      };
    },
  }));
};

/** Клуб дыма или пыли: растет, поднимается и тает */
export const pushPuff = (elements, {at, tint, sizePx, startMs = 0, lifeMs, alpha, riseY = 0, shiftX = 0, spin = 0, order = 0, layer}) => {
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint, sizePx, startMs, durationMs: lifeMs, order, layer,
    pose: (local) => {
      const travel = easeOutCubic(local);
      return {
        x: at.x + shiftX * travel,
        y: at.y - riseY * travel,
        scale: lerp(0.45, 1, travel),
        alpha: alpha * fadeInOut(local, 0.12, 0.6),
        rotation: spin * local,
      };
    },
  }));
};

/** Шестиугольная волна: плотный фронт с шлейфом и светлая линия */
export const pushHexWave = (elements, {at, hexSize, color, endFactor, durationMs, alpha, startMs = 0, hexRotation = 0, order = 0}) => {
  const baseSizePx = HEX_WAVE_TEXTURE_SIZE * resolveHexWaveScale(hexSize);
  [
    {textureKey: HEX_WAVE_TEXTURE_KEYS.BAND, tint: color, order},
    {textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: lighten(color, 0.68), order: order + 0.5},
  ].forEach((layer) => {
    elements.push(createElement({
      ...layer, sizePx: baseSizePx, startMs, durationMs,
      pose: (local) => ({
        ...at,
        scale: lerp(0.8, endFactor, easeOutCubic(local)),
        alpha: alpha * fadeOutFrom(local, 0.45),
        rotation: hexRotation,
      }),
    }));
  });
};

/**
 * ВЗМАХ оружия: свет на кончике и слетающие с него искры. Время — от начала удара, тем же
 * ходом, что и само оружие (см. HexGridRenderer.updateMeleeStrike)
 *
 * @param {Object} params
 * @param {Object} params.geometry — см. buildMeleeStrikeGeometry
 * @param {number} params.color
 * @param {function(): number} [params.rng]
 */
export const buildMeleeSlashCastPlan = ({geometry, color, rng = Math.random}) => {
  const {startAtMs, durationMs} = MELEE_ANIMATION.strike.slash;
  const {tipGlow, tipCore, embers, dust} = MELEE_SLASH_CAST;
  const size = geometry.hexSize;
  const tipRadius = geometry.handleRadius + geometry.weaponLength;
  const tipAt = (swing) => {
    const {angle} = resolveMeleeSlashState(geometry, swing);
    return {x: geometry.pivot.x + Math.cos(angle) * tipRadius, y: geometry.pivot.y + Math.sin(angle) * tipRadius, angle};
  };
  const elements = [];

  [
    {config: tipGlow, tint: color, order: 1},
    {config: tipCore, tint: COLORS.white, order: 2},
  ].forEach(({config, tint, order}) => {
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint, sizePx: size * config.sizeFactor, startMs: startAtMs, durationMs, order,
      pose: (local) => {
        const {x, y} = tipAt(local);
        return {x, y, scale: 0.7 + 0.6 * Math.sin(Math.PI * local), alpha: config.alpha * fadeInOut(local, 0.15, 0.25)};
      },
    }));
  });

  // Искры слетают с кончика по касательной к дуге, куда он идет в этот миг
  const sign = geometry.endAngle >= geometry.startAngle ? 1 : -1;
  for (let index = 0; index < embers.count; index += 1) {
    const swing = lerp(embers.swingFrom, embers.swingTo, (index + rng()) / embers.count);
    const {x, y, angle} = tipAt(swing);
    pushStreak(elements, {
      at: {x, y},
      angle: angle + sign * (Math.PI / 2) + (rng() - 0.5) * 0.7,
      distance: size * embers.flyFactor * (0.6 + 0.6 * rng()),
      lengthPx: size * embers.lengthFactor,
      thicknessPx: size * embers.thicknessFactor,
      tint: index % 3 === 0 ? COLORS.white : lighten(color, 0.3),
      startMs: startAtMs + durationMs * swing,
      lifeMs: embers.lifeMs,
      order: 3,
    });
  }

  // Рывок выбивает пыль из-под ног: назад от цели
  const heelAt = {x: geometry.pivot.x - geometry.lunge.x, y: geometry.pivot.y - geometry.lunge.y + size * 0.2};
  for (let index = 0; index < dust.count; index += 1) {
    pushPuff(elements, {
      at: heelAt,
      tint: FX.dust,
      sizePx: size * dust.sizeFactor,
      lifeMs: dust.lifeMs,
      alpha: dust.alpha,
      riseY: size * 0.1,
      shiftX: -Math.cos(geometry.direction) * size * dust.flyFactor * (0.6 + rng()),
      spin: rng() - 0.5,
      layer: 'ground',
    });
  }

  return finishPlan({elements});
};

/**
 * ПОПАДАНИЕ удара
 *
 * @param {Object} params
 * @param {{x, y}} params.at — точка попадания
 * @param {number} params.direction — угол удара
 * @param {number} params.hexSize
 * @param {number} params.color
 * @param {number} params.tier — см. resolveMeleeImpactTier
 * @param {number} [params.hexRotation]
 * @param {function(): number} [params.rng]
 */
export const buildMeleeImpactCastPlan = ({at, direction, hexSize, color, tier, hexRotation = 0, rng = Math.random}) => {
  const cast = resolveMeleeImpactCast(tier);
  const size = hexSize;
  const isHarmless = tier === 0;
  // Удар без потерь — звон о защиту: стальной, не цветом игрока
  const mainColor = isHarmless ? FX.steel : color;
  const lightColor = lighten(mainColor);
  const elements = [];

  pushFlash(elements, {
    at, textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: lightColor, sizePx: size * cast.bloomSizeFactor,
    durationMs: cast.bloom.durationMs, alpha: cast.bloom.alpha, order: 1,
  });
  for (let index = 0; index < cast.ringCount; index += 1) {
    pushFlash(elements, {
      at, textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: index ? lightColor : mainColor, sizePx: size * cast.ringSizeFactor,
      startMs: index * cast.ring.gapMs, durationMs: cast.ring.durationMs, alpha: cast.ring.alpha, order: 1, layer: 'ground', fromScale: 0.15,
    });
  }
  for (let index = 0; index < cast.waveCount; index += 1) {
    pushHexWave(elements, {
      at, hexSize: size, color: mainColor, endFactor: cast.waveEndFactor, durationMs: cast.wave.durationMs, alpha: cast.wave.alpha, hexRotation, order: 2,
    });
  }
  pushFlash(elements, {
    at, textureKey: MELEE_TEXTURE_KEYS.BURST, tint: mainColor, sizePx: size * cast.starSizeFactor,
    durationMs: cast.star.durationMs, order: 3, fromScale: 0.3, rotation: direction,
  });
  pushFlash(elements, {
    at, tint: COLORS.white, sizePx: size * cast.flashSizeFactor, durationMs: cast.flash.durationMs, order: 4,
  });

  // Рассекающая черта поперек удара: белая, с ореолом цветом игрока
  for (let index = 0; index < cast.cutCount; index += 1) {
    const angle = direction + Math.PI / 2 + (index ? -cast.cut.spreadRad : cast.cut.spreadRad * (rng() - 0.5));
    [
      {tint: mainColor, thickness: 2.2, order: 5},
      {tint: COLORS.white, thickness: 1, order: 6},
    ].forEach((layer) => {
      elements.push(createElement({
        textureKey: STREAK, tint: layer.tint, sizePx: size * cast.cutLengthFactor, startMs: index * 40, durationMs: cast.cut.durationMs, order: layer.order,
        pose: (local) => ({
          ...at,
          scaleX: lerp(0.3, 1, easeOutCubic(clamp01(local * 3))),
          scaleY: ((size * cast.cut.thicknessFactor * layer.thickness) * STREAK_ASPECT) / (size * cast.cutLengthFactor) * (1 - local),
          alpha: 1 - local * local,
          rotation: angle,
        }),
      }));
    });
  }

  for (let index = 0; index < cast.sparkCount; index += 1) {
    const isForward = rng() < cast.sparks.forwardShare;
    const angle = isForward ? direction + (rng() - 0.5) * cast.sparks.coneRad : rng() * TWO_PI;
    pushStreak(elements, {
      at,
      angle,
      distance: size * cast.sparkDistanceFactor * (0.55 + 0.6 * rng()),
      lengthPx: size * cast.sparks.lengthFactor,
      thicknessPx: size * cast.sparks.thicknessFactor,
      tint: index % 3 === 0 ? COLORS.white : (index % 3 === 1 ? lightColor : mainColor),
      lifeMs: cast.sparks.lifeMs * (0.7 + 0.5 * rng()),
      order: 7,
    });
  }

  return finishPlan({
    elements,
    shake: cast.shakeMs > 0 ? {durationMs: cast.shakeMs, intensity: cast.shakeIntensity} : null,
  });
};

/**
 * ПУСК ЗАЛПА СТРЕЛ: вспышка и кольцо у стрелка, искры по направлению выстрела
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.direction — угол на цель
 * @param {number} params.hexSize
 * @param {number} params.color
 * @param {number} [params.delayMs] — когда залп уходит (сжатие темпа уже учтено)
 * @param {function(): number} [params.rng]
 */
export const buildArrowLaunchCastPlan = ({at, direction, hexSize, color, delayMs = 0, rng = Math.random}) => {
  const {glow, ring, sparks} = ARROW_CAST.launch;
  const elements = [];
  pushFlash(elements, {
    at, textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: color, sizePx: hexSize * ring.sizeFactor, startMs: delayMs,
    durationMs: ring.durationMs, alpha: ring.alpha, layer: 'ground', fromScale: 0.2,
  });
  pushFlash(elements, {at, tint: color, sizePx: hexSize * glow.sizeFactor, startMs: delayMs, durationMs: glow.durationMs, alpha: glow.alpha, order: 1});
  pushFlash(elements, {at, tint: COLORS.white, sizePx: hexSize * glow.sizeFactor * 0.45, startMs: delayMs, durationMs: glow.durationMs * 0.7, order: 2});
  for (let index = 0; index < sparks.count; index += 1) {
    pushStreak(elements, {
      at,
      angle: direction + (rng() - 0.5) * sparks.coneRad * 2,
      distance: hexSize * sparks.flyFactor * (0.6 + 0.6 * rng()),
      lengthPx: hexSize * sparks.lengthFactor,
      thicknessPx: hexSize * sparks.thicknessFactor,
      tint: index % 2 ? lighten(color) : COLORS.white,
      startMs: delayMs,
      lifeMs: sparks.lifeMs,
      order: 3,
    });
  }
  return finishPlan({elements});
};

/**
 * Одна стрела долетела: на попадании искры назад от удара, на промахе пыль
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.angle — куда летела стрела
 * @param {number} params.hexSize
 * @param {number} params.color
 * @param {boolean} [params.isMiss]
 * @param {function(): number} [params.rng]
 */
export const buildArrowHitCastPlan = ({at, angle, hexSize, color, isMiss = false, rng = Math.random}) => {
  const elements = [];
  if (isMiss) {
    const {puff} = ARROW_CAST.miss;
    pushPuff(elements, {
      at, tint: FX.dust, sizePx: hexSize * puff.sizeFactor, lifeMs: puff.durationMs, alpha: puff.alpha,
      riseY: hexSize * 0.12, spin: rng() - 0.5, layer: 'ground',
    });
    return finishPlan({elements});
  }

  const {star, sparks} = ARROW_CAST.hit;
  pushFlash(elements, {
    at, textureKey: MELEE_TEXTURE_KEYS.BURST, tint: lighten(color, 0.35), sizePx: hexSize * star.sizeFactor,
    durationMs: star.durationMs, rotation: angle, order: 1,
  });
  for (let index = 0; index < sparks.count; index += 1) {
    pushStreak(elements, {
      at,
      angle: angle + Math.PI + (rng() - 0.5) * sparks.coneRad * 2,
      distance: hexSize * sparks.flyFactor * (0.6 + 0.6 * rng()),
      lengthPx: hexSize * sparks.lengthFactor,
      thicknessPx: hexSize * sparks.thicknessFactor,
      tint: index % 2 ? COLORS.white : lighten(color, 0.3),
      lifeMs: sparks.lifeMs,
      order: 2,
    });
  }
  return finishPlan({elements});
};

/**
 * Последняя стрела залпа: шестиугольная волна и разлет искр по ячейке
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.hexSize
 * @param {number} params.color
 * @param {number} params.arrowCount
 * @param {number} [params.hexRotation]
 * @param {function(): number} [params.rng]
 */
export const buildArrowVolleyCastPlan = ({at, hexSize, color, arrowCount, hexRotation = 0, rng = Math.random}) => {
  const {bloom, star, wave, sparks} = ARROW_CAST.volley;
  const elements = [];
  pushFlash(elements, {at, tint: color, sizePx: hexSize * bloom.sizeFactor, durationMs: bloom.durationMs, alpha: bloom.alpha});
  pushHexWave(elements, {at, hexSize, color, endFactor: wave.endFactor, durationMs: wave.durationMs, alpha: wave.alpha, hexRotation, order: 1});
  pushFlash(elements, {
    at, textureKey: MELEE_TEXTURE_KEYS.BURST, tint: lighten(color, 0.3), sizePx: hexSize * star.sizeFactor, durationMs: star.durationMs, order: 2, fromScale: 0.3,
  });
  const count = Math.min(sparks.max, Math.max(4, arrowCount * sparks.perArrow));
  for (let index = 0; index < count; index += 1) {
    pushStreak(elements, {
      at,
      angle: (index + rng()) / count * TWO_PI,
      distance: hexSize * sparks.flyFactor * (0.6 + 0.55 * rng()),
      lengthPx: hexSize * sparks.lengthFactor,
      thicknessPx: hexSize * sparks.thicknessFactor,
      tint: index % 2 ? COLORS.white : lighten(color, 0.3),
      lifeMs: sparks.lifeMs,
      order: 3,
    });
  }
  return finishPlan({elements});
};

/**
 * ПУСК КАТАПУЛЬТЫ: огонь и дым у катапульты, кольцо по земле
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.hexSize
 * @param {number} [params.delayMs]
 * @param {function(): number} [params.rng]
 */
export const buildBoulderLaunchCastPlan = ({at, hexSize, delayMs = 0, rng = Math.random}) => {
  const {flash, ring, smoke} = BOULDER_CAST.launch;
  const elements = [];
  pushFlash(elements, {
    at, textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: FX.fireLight, sizePx: hexSize * ring.sizeFactor, startMs: delayMs,
    durationMs: ring.durationMs, alpha: ring.alpha, layer: 'ground', fromScale: 0.2,
  });
  for (let index = 0; index < smoke.count; index += 1) {
    pushPuff(elements, {
      at, tint: FX.smoke, sizePx: hexSize * smoke.sizeFactor, startMs: delayMs + index * 30, lifeMs: smoke.lifeMs, alpha: smoke.alpha,
      riseY: hexSize * smoke.riseFactor, shiftX: hexSize * smoke.spreadFactor * (rng() - 0.5) * 2, spin: rng() - 0.5,
    });
  }
  pushFlash(elements, {at, tint: FX.fire, sizePx: hexSize * flash.sizeFactor, startMs: delayMs, durationMs: flash.durationMs, alpha: flash.alpha, order: 1});
  pushFlash(elements, {at, tint: FX.fireCore, sizePx: hexSize * flash.sizeFactor * 0.5, startMs: delayMs, durationMs: flash.durationMs * 0.7, order: 2});
  return finishPlan({elements});
};

/**
 * ПРИЦЕЛ на цели катапульты: кольцо сжимается к контуру ячейки и пульсирует, пока камни в
 * воздухе
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.hexSize
 * @param {number} params.landAtMs — когда падает последний камень
 * @param {number} [params.hexRotation]
 */
export const buildBoulderTargetCastPlan = ({at, hexSize, landAtMs, hexRotation = 0}) => {
  const {target} = BOULDER_CAST;
  const elements = [];
  const baseSizePx = HEX_WAVE_TEXTURE_SIZE * (hexSize / HEX_WAVE_RADIUS_PX);
  const durationMs = landAtMs + target.leaveMs;
  const ringPose = (local, elapsedMs, alpha) => {
    const share = clamp01(elapsedMs / landAtMs);
    const pulse = (Math.sin(share * target.pulses * TWO_PI - Math.PI / 2) + 1) / 2;
    return {
      ...at,
      scale: lerp(target.fromFactor, target.toFactor, easeInOutSine(share)),
      rotation: hexRotation,
      alpha: alpha * clamp01(share * 5) * (0.55 + 0.45 * pulse) * (elapsedMs > landAtMs ? 1 - (elapsedMs - landAtMs) / target.leaveMs : 1),
    };
  };
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: FX.target, sizePx: hexSize * target.groundSizeFactor, startMs: 0, durationMs, layer: 'ground',
    pose: (local, elapsedMs) => ({...at, alpha: target.groundAlpha * clamp01(elapsedMs / landAtMs)}),
  }));
  elements.push(createElement({
    textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint: FX.target, sizePx: baseSizePx, startMs: 0, durationMs, order: 1,
    pose: (local, elapsedMs) => ringPose(local, elapsedMs, target.glowAlpha),
  }));
  elements.push(createElement({
    textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: lighten(FX.target, 0.45), sizePx: baseSizePx, startMs: 0, durationMs, order: 2,
    pose: (local, elapsedMs) => ringPose(local, elapsedMs, target.coreAlpha),
  }));
  return finishPlan({elements});
};

/**
 * ПАДЕНИЕ ВАЛУНА
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.hexSize
 * @param {number} params.boulderCount — камней в залпе: чем больше, тем мельче каждый эффект
 * @param {boolean} [params.isMiss]
 * @param {function(): number} [params.rng]
 */
export const buildBoulderImpactCastPlan = ({at, hexSize, boulderCount = 1, isMiss = false, rng = Math.random}) => {
  const {impact, miss} = BOULDER_CAST;
  const scale = boulderCount >= impact.crowdFrom ? impact.crowdScale : 1;
  const size = hexSize * scale;
  const elements = [];
  const pushEmbers = (count) => {
    for (let index = 0; index < count; index += 1) {
      pushStreak(elements, {
        at,
        angle: (index + rng()) / count * TWO_PI,
        distance: size * impact.embers.flyFactor * (0.55 + 0.6 * rng()),
        lengthPx: size * impact.embers.lengthFactor,
        thicknessPx: size * impact.embers.thicknessFactor,
        tint: index % 2 ? FX.fireLight : FX.fire,
        lifeMs: impact.embers.lifeMs * (0.75 + 0.45 * rng()),
        order: 4,
        squash: impact.embers.squash,
      });
    }
  };

  if (isMiss) {
    pushFlash(elements, {
      at, textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: FX.dust, sizePx: size * miss.ring.sizeFactor,
      durationMs: miss.ring.durationMs, alpha: miss.ring.alpha, layer: 'ground', fromScale: 0.2,
    });
    pushEmbers(miss.embers.count);
    return finishPlan({elements});
  }

  // Выжженное пятно остается дольше всего: оно на земле, под отрядами
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint: FX.scorch, sizePx: size * impact.scorch.sizeFactor,
    startMs: 0, durationMs: impact.scorch.durationMs, layer: 'ground', order: 1,
    pose: (local) => ({
      ...at,
      scaleY: lerp(0.55, 1, easeOutCubic(clamp01(local * 6))) * 0.8,
      scaleX: lerp(0.55, 1, easeOutCubic(clamp01(local * 6))),
      alpha: impact.scorch.alpha * fadeOutFrom(local, impact.scorch.holdShare),
    }),
  }));
  pushFlash(elements, {
    at, textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: FX.fireLight, sizePx: size * impact.ring.sizeFactor,
    durationMs: impact.ring.durationMs, alpha: impact.ring.alpha, layer: 'ground', order: 2, fromScale: 0.15,
  });
  for (let index = 0; index < impact.smoke.count; index += 1) {
    pushPuff(elements, {
      at, tint: FX.smoke, sizePx: size * impact.smoke.sizeFactor, startMs: 40 + index * 50, lifeMs: impact.smoke.lifeMs, alpha: impact.smoke.alpha,
      riseY: size * impact.smoke.riseFactor, shiftX: size * impact.smoke.spreadFactor * (rng() - 0.5) * 2, spin: rng() - 0.5, order: 1,
    });
  }
  pushFlash(elements, {
    at, textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: FX.fire, sizePx: size * impact.fireball.sizeFactor,
    durationMs: impact.fireball.durationMs, alpha: impact.fireball.alpha, order: 2,
  });
  pushFlash(elements, {at, tint: FX.fireCore, sizePx: size * impact.flash.sizeFactor, durationMs: impact.flash.durationMs, order: 3});
  pushEmbers(Math.max(3, Math.round(impact.embers.count * scale)));
  return finishPlan({elements});
};

/**
 * Последний валун залпа попал: большая вспышка, ударная волна, волна по ячейке и сильный
 * толчок камеры
 *
 * @param {Object} params
 * @param {{x, y}} params.at
 * @param {number} params.hexSize
 * @param {number} [params.hexRotation]
 * @param {function(): number} [params.rng]
 */
export const buildBoulderVolleyCastPlan = ({at, hexSize, hexRotation = 0, rng = Math.random}) => {
  const {bloom, ring, wave, sparks, shake} = BOULDER_CAST.volley;
  const elements = [];
  pushFlash(elements, {
    at, textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: FX.fire, sizePx: hexSize * ring.sizeFactor, durationMs: ring.durationMs,
    alpha: ring.alpha, layer: 'ground', order: 3, fromScale: 0.12,
  });
  pushHexWave(elements, {at, hexSize, color: FX.fire, endFactor: wave.endFactor, durationMs: wave.durationMs, alpha: wave.alpha, hexRotation, order: 1});
  pushFlash(elements, {at, tint: FX.fireLight, sizePx: hexSize * bloom.sizeFactor, durationMs: bloom.durationMs, alpha: bloom.alpha, order: 2});
  for (let index = 0; index < sparks.count; index += 1) {
    pushStreak(elements, {
      at,
      angle: (index + rng()) / sparks.count * TWO_PI,
      distance: hexSize * sparks.flyFactor * (0.55 + 0.6 * rng()),
      lengthPx: hexSize * sparks.lengthFactor,
      thicknessPx: hexSize * sparks.thicknessFactor,
      tint: index % 3 === 0 ? FX.fireCore : (index % 3 === 1 ? FX.fireLight : FX.fire),
      lifeMs: sparks.lifeMs * (0.75 + 0.4 * rng()),
      order: 3,
      squash: sparks.squash,
    });
  }
  return finishPlan({elements, shake});
};
